import React, { useState, useRef, useEffect } from "react";
import { useWorkspaceState } from "../../../src/components/workspace/hooks/core/useWorkspaceState";
import { useModelStore } from "../../../src/store/useModelStore";
import { useCursorManager } from "./hooks/useCursorManager";
import GridFreameImporter from "../workspace/GridFrameImporter";
import { useGlobalSelectionStore } from "../../store/useGlobalSelectionStore";
import { usePropertyStore } from "@/store/usePropertyStore";
import { useCursorMode } from "@/hooks/useGlobalSelection";
import { useCaptureAreaStore } from "@/hooks/useCaptureArea";

interface Geometry {
  nodes: Array<{ id: number; x: number; y: number; z: number }>;
  elements: Array<{ id: number; start: number; end: number; type: string }>;
}

const ModelingMenu: React.FC = () => {
  const [activeCreateMode, setActiveCreateMode] = useState<
    "geometry" | "node" | "beam" | "prep" | "divide" | "capture" | null
  >(null);
  const { setCursorMode, clearCursorMode } = useCursorMode();
  const { setCursorByState } = useCursorManager();

  // Geometry modal states
  const [showGeometryModal, setShowGeometryModal] = useState(false);

  const isProcessingRef = useRef(false);
  const { state, callbacks } = useWorkspaceState();
  const modelStore = useModelStore();
  const { isCaptureMode, startCapture, cancelCapture } = useCaptureAreaStore();

  // Listen for Escape key to reset creation modes
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();

        let wasSomethingActive = false;

        // Cancel Node Creation Mode
        if (state.isNodeCreationMode) {
          callbacks.setIsNodeCreationMode(false);
          clickToolbarButton(["Create Node", "Node"]);
          wasSomethingActive = true;
        }

        // Cancel Beam Creation Mode
        if (state.isBeamCreationMode) {
          callbacks.setIsBeamCreationMode(false);
          modelStore.cancelBeamCreation();
          clickToolbarButton(["Create Beam", "Beam"]);
          wasSomethingActive = true;
        }

        // Cancel Prep Mode
        if (modelStore.isPrepMode) {
          modelStore.cancelPrepMode();
          clickToolbarButton(["Prep Mode"]);
          wasSomethingActive = true;
        }

        // Cancel Divide Beam Mode
        if (modelStore.isDivideBeamMode) {
          modelStore.cancelDivideBeamMode();
          clickToolbarButton(["Divide Beam"]);
          wasSomethingActive = true;
        }

        // Cancel Capture Mode
        if (isCaptureMode) {
          cancelCapture();
          setActiveCreateMode(null);
          wasSomethingActive = true;
        }

        // Cancel Geometry Modal
        if (showGeometryModal) {
          setShowGeometryModal(false);
          wasSomethingActive = true;
        }

        // Reset active create mode
        if (activeCreateMode) {
          setActiveCreateMode(null);
          wasSomethingActive = true;
        }

        // Reset cursor(hide the message)
        clearCursorMode();
        setCursorByState(null);

        // Clear any beam/node selections
        const clearSelection = useModelStore.getState().clearSelection;
        if (clearSelection) {
          clearSelection();
        }

        useGlobalSelectionStore.getState().clearTableSelection();
      }
    };

    window.addEventListener("keydown", handleEscapeKey);
    return () => window.removeEventListener("keydown", handleEscapeKey);
  }, [
    callbacks,
    modelStore,
    clearCursorMode,
    setCursorByState,
    state.isNodeCreationMode,
    state.isBeamCreationMode,
    modelStore.isPrepMode,
    modelStore.isDivideBeamMode,
    showGeometryModal,
    activeCreateMode,
    isCaptureMode,
    cancelCapture,
  ]);

  useEffect(() => {
    if (state.isNodeCreationMode) {
      setActiveCreateMode("node");
      setCursorByState("node");
    } else if (state.isBeamCreationMode) {
      setActiveCreateMode("beam");
      setCursorByState("beam");
    } else if (modelStore.isPrepMode) {
      setActiveCreateMode("prep");
      setCursorByState("prep");
    } else if (modelStore.isDivideBeamMode) {
      setActiveCreateMode("divide");
      setCursorByState("divide");
    } else if (
      !state.isNodeCreationMode &&
      !state.isBeamCreationMode &&
      !modelStore.isPrepMode &&
      !modelStore.isDivideBeamMode &&
      activeCreateMode !== "geometry" &&
      activeCreateMode !== "prep"
    ) {
      setActiveCreateMode(null);
      setCursorByState(null);
    }
  }, [
    state.isNodeCreationMode,
    state.isBeamCreationMode,
    modelStore.isPrepMode,
    modelStore.isDivideBeamMode,
    setCursorByState,
  ]);

  function handleImportGeometry(
    geometry: Geometry,
    clearPrevious: boolean
  ): void {
    const nodeIdMap: Record<number, string> = {};
    const modelStore = useModelStore.getState();

    // STEP 1: IMPORT NODES (same as before)
    geometry.nodes.forEach((node) => {
      const rx = Math.round(node.x * 100) / 100;
      const ry = Math.round(node.y * 100) / 100;
      const rz = Math.round(node.z * 100) / 100;

      const existing = modelStore.nodes.find(
        (n) => n.x === rx && n.y === ry && n.z === rz
      );

      if (existing) {
        nodeIdMap[node.id] = existing.id;
      } else {
        modelStore.addNode({ x: rx, y: ry, z: rz });
        const added = useModelStore
          .getState()
          .nodes.find((n) => n.x === rx && n.y === ry && n.z === rz);

        if (added) nodeIdMap[node.id] = added.id;
      }
    });

    // BUILD NEW BEAM ARRAY
    const newBeams: any[] = [];

    // Get current nextBeamId from store and track locally
    let currentBeamId = useModelStore.getState().nextBeamId;

    geometry.elements.forEach((e) => {
      const s = nodeIdMap[e.start];
      const t = nodeIdMap[e.end];

      if (s && t) {
        newBeams.push({
          id: String(currentBeamId),
          startNodeId: s,
          endNodeId: t,
        });
        currentBeamId++;
      }
    });

    // Update the store's nextBeamId to the new value
    useModelStore.setState({ nextBeamId: currentBeamId });

    // MERGE OR REPLACE BEAMS
    if (clearPrevious) {
      // remove everything
      modelStore.setBeams(newBeams);
    } else {
      // APPEND instead of replace
      modelStore.setBeams([...modelStore.beams, ...newBeams]);
    }

    setTimeout(() => {
      usePropertyStore.getState().autoApplyPendingProperties();
    }, 150);
  }

  const clickToolbarButton = (searchTexts: string[]) => {
    const buttons = document.querySelectorAll("button");
    for (const text of searchTexts) {
      const button = Array.from(buttons).find((btn) => {
        const btnText = btn.textContent?.trim();
        const isMenuButton = btn.closest('[class*="bg-gray-8"]') !== null;
        if (isMenuButton) return false;
        return btnText === text || btnText?.includes(text);
      });
      if (button) {
        button.click();
        return true;
      }
    }
    return false;
  };

  const handleGeometryClick = () => {
    setShowGeometryModal(true);
    setActiveCreateMode("geometry");
  };

  const handleNodeClick = () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    useGlobalSelectionStore.getState().clearTableSelection();

    const isNodeActive = state.isNodeCreationMode;

    if (isNodeActive) {
      callbacks.setIsNodeCreationMode(false);
      clearCursorMode();
      setCursorByState(null);
    } else {
      callbacks.setIsNodeCreationMode(true);
      setCursorMode("node-creation");
      setCursorByState("node");

      if (state.isBeamCreationMode) {
        callbacks.setIsBeamCreationMode(false);
        clickToolbarButton(["Create Beam", "Beam"]);
      }
      if (modelStore.isPrepMode) {
        modelStore.cancelPrepMode();
      }
      if (modelStore.isDivideBeamMode) {
        modelStore.cancelDivideBeamMode();
        clickToolbarButton(["Divide Beam"]);
      }
    }

    clickToolbarButton(["Create Node", "Node"]);

    setTimeout(() => {
      isProcessingRef.current = false;
    }, 200);
  };

  const handleBeamClick = () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    useGlobalSelectionStore.getState().clearTableSelection();

    const isBeamActive = state.isBeamCreationMode;

    if (isBeamActive) {
      callbacks.setIsBeamCreationMode(false);
      clearCursorMode();
      setCursorByState(null);
      modelStore.cancelBeamCreation();
    } else {
      callbacks.setIsBeamCreationMode(true);
      setCursorMode("beam-creation");
      setCursorByState("beam");
      modelStore.startBeamCreation();

      if (state.isNodeCreationMode) {
        callbacks.setIsNodeCreationMode(false);
        clickToolbarButton(["Create Node", "Node"]);
      }
      if (modelStore.isPrepMode) {
        modelStore.cancelPrepMode();
      }
      if (modelStore.isDivideBeamMode) {
        modelStore.cancelDivideBeamMode();
        clickToolbarButton(["Divide Beam"]);
      }
    }

    clickToolbarButton(["Create Beam", "Beam"]);

    setTimeout(() => {
      isProcessingRef.current = false;
    }, 200);
  };

  const handlePrepModeClick = () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    useGlobalSelectionStore.getState().clearTableSelection();

    const isPrepActive = modelStore.isPrepMode;

    if (isPrepActive) {
      modelStore.cancelPrepMode();
      callbacks.setIsNodeCreationMode(false);
      callbacks.setIsBeamCreationMode(false);
      clearCursorMode();
      setCursorByState(null);
    } else {
      modelStore.startPrepMode();
      setCursorMode("prep-mode");
      setCursorByState("prep");

      if (state.isNodeCreationMode) {
        callbacks.setIsNodeCreationMode(false);
        clickToolbarButton(["Create Node", "Node"]);
      }
      if (state.isBeamCreationMode) {
        callbacks.setIsBeamCreationMode(false);
        clickToolbarButton(["Create Beam", "Beam"]);
        modelStore.cancelBeamCreation();
      }
      if (modelStore.isDivideBeamMode) {
        modelStore.cancelDivideBeamMode();
        clickToolbarButton(["Divide Beam"]);
      }
    }

    clickToolbarButton(["Prep Mode"]);

    setTimeout(() => {
      isProcessingRef.current = false;
    }, 200);
  };

  const handleDivideBeamClick = () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    useGlobalSelectionStore.getState().clearTableSelection();

    const isDivideActive = modelStore.isDivideBeamMode;

    if (isDivideActive) {
      modelStore.cancelDivideBeamMode();
      clearCursorMode();
      setCursorByState(null);
    } else {
      modelStore.startDivideBeamMode();
      setCursorMode("divide-beam");
      setCursorByState("divide");

      // Disable other modes
      if (state.isNodeCreationMode) {
        callbacks.setIsNodeCreationMode(false);
        clickToolbarButton(["Create Node", "Node"]);
      }

      if (state.isBeamCreationMode) {
        callbacks.setIsBeamCreationMode(false);
        clickToolbarButton(["Create Beam", "Beam"]);
        modelStore.cancelBeamCreation();
      }

      if (modelStore.isPrepMode) {
        modelStore.cancelPrepMode();
      }
    }

    clickToolbarButton(["Divide Beam"]);

    setTimeout(() => {
      isProcessingRef.current = false;
    }, 200);
  };

  const handleCaptureClick = () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    useGlobalSelectionStore.getState().clearTableSelection();

    // Cancel all other modes
    if (state.isNodeCreationMode) {
      callbacks.setIsNodeCreationMode(false);
      clickToolbarButton(["Create Node", "Node"]);
    }
    if (state.isBeamCreationMode) {
      callbacks.setIsBeamCreationMode(false);
      clickToolbarButton(["Create Beam", "Beam"]);
      modelStore.cancelBeamCreation();
    }
    if (modelStore.isPrepMode) {
      modelStore.cancelPrepMode();
      clickToolbarButton(["Prep Mode"]);
    }
    if (modelStore.isDivideBeamMode) {
      modelStore.cancelDivideBeamMode();
      clickToolbarButton(["Divide Beam"]);
    }

    // Clear cursor and start capture
    clearCursorMode();
    setCursorByState(null);
    setActiveCreateMode("capture");
    startCapture();

    setTimeout(() => {
      isProcessingRef.current = false;
    }, 200);
  };

  const handleLoadViewClick = () => {};
  const handleLoadAddClick = () => {};
  const handleSupportViewClick = () => {};
  const handleSupportAddClick = () => {};
  const handleSectionViewClick = () => {};
  const handleSectionAddClick = () => {};

  return (
    <>
      {/* Geometry Modal */}
      {showGeometryModal && (
        <GridFreameImporter
          onImport={handleImportGeometry}
          onClose={() => {
            setShowGeometryModal(false);
            setActiveCreateMode(null); // fix: allow reopening
          }}
        />
      )}

      <div className="px-5 py-2 bg-gradient-to-b from-gray-850 to-gray-900">
        <div className="flex items-center gap-6">
          {/* ==================== CREATE SECTION ==================== */}
          <div className="flex items-center gap-2">
            <div className="text-[10px] text-gray-500 font-semibold writing-mode-vertical h-16 flex items-center tracking-wide">
              CREATE
            </div>
            <div className="flex gap-2">
              {/* Geometry */}
              <button
                onClick={handleGeometryClick}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all group ${
                  activeCreateMode === "geometry"
                    ? "bg-gradient-to-br from-purple-500/30 to-purple-600/20 text-purple-200 border border-purple-500/60"
                    : "bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-purple-500"
                }`}
              >
                <svg
                  className={`w-5 h-5 ${
                    activeCreateMode === "geometry"
                      ? "text-purple-300"
                      : "text-purple-400 group-hover:text-purple-300"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 2L2 7v10l10 5 10-5V7L12 2z"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M2 7l10 5m0 0l10-5m-10 5v10"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <span
                  className={`text-xs font-medium ${
                    activeCreateMode === "geometry"
                      ? "text-purple-200"
                      : "text-gray-300 group-hover:text-white"
                  }`}
                >
                  Geometry
                </span>
              </button>

              {/* Node */}
              <button
                aria-pressed={state.isNodeCreationMode}
                onClick={handleNodeClick}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all group ${
                  state.isNodeCreationMode
                    ? "bg-gradient-to-br from-blue-500/30 to-blue-600/20 text-blue-200 border border-blue-500/60"
                    : "bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-blue-500"
                }`}
                title="Create Node (N)"
              >
                <svg
                  className={`w-5 h-5 ${
                    state.isNodeCreationMode
                      ? "text-blue-300"
                      : "text-blue-400 group-hover:text-blue-300"
                  }`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="4" />
                  <circle
                    cx="12"
                    cy="12"
                    r="2"
                    fill="none"
                    stroke="white"
                    strokeWidth="1"
                  />
                </svg>
                <span
                  className={`text-xs font-medium ${
                    state.isNodeCreationMode
                      ? "text-blue-200"
                      : "text-gray-300 group-hover:text-white"
                  }`}
                >
                  Node
                </span>
              </button>

              {/* Beam */}
              <button
                aria-pressed={state.isBeamCreationMode}
                onClick={handleBeamClick}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all group ${
                  state.isBeamCreationMode
                    ? "bg-gradient-to-br from-green-500/30 to-green-600/20 text-green-200 border border-green-500/60"
                    : "bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-green-500"
                }`}
                title="Create Beam (B)"
              >
                <svg
                  className={`w-5 h-5 ${
                    state.isBeamCreationMode
                      ? "text-green-300"
                      : "text-green-400 group-hover:text-green-300"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <line
                    x1="4"
                    y1="12"
                    x2="20"
                    y2="12"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <circle cx="4" cy="12" r="2" fill="currentColor" />
                  <circle cx="20" cy="12" r="2" fill="currentColor" />
                </svg>
                <span
                  className={`text-xs font-medium ${
                    state.isBeamCreationMode
                      ? "text-green-200"
                      : "text-gray-300 group-hover:text-white"
                  }`}
                >
                  Beam
                </span>
              </button>

              {/* Prep Mode */}
              <button
                aria-pressed={modelStore.isPrepMode}
                onClick={handlePrepModeClick}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all group ${
                  modelStore.isPrepMode
                    ? "bg-gradient-to-br from-cyan-500/30 to-cyan-600/20 text-cyan-200 border border-cyan-500/60"
                    : "bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-cyan-500"
                }`}
                title="Perpendicular Beam Creation (P)"
              >
                <svg
                  className={`w-5 h-5 ${
                    modelStore.isPrepMode
                      ? "text-cyan-300"
                      : "text-cyan-400 group-hover:text-cyan-300"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {/* Left underscore */}
                  <path d="M6 18h6" strokeWidth="2" strokeLinecap="round" />

                  {/* Vertical L */}
                  <path d="M12 6v12" strokeWidth="2" strokeLinecap="round" />

                  {/* Right underscore */}
                  <path d="M12 18h6" strokeWidth="2" strokeLinecap="round" />
                </svg>

                <span
                  className={`text-xs font-medium ${
                    modelStore.isPrepMode
                      ? "text-cyan-200"
                      : "text-gray-300 group-hover:text-white"
                  }`}
                >
                  Prep Mode
                </span>
              </button>

              {/* MID NODE */}
              <button
                aria-pressed={modelStore.isDivideBeamMode}
                onClick={handleDivideBeamClick}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all group ${
                  modelStore.isDivideBeamMode
                    ? "bg-gradient-to-br from-orange-500/30 to-orange-600/20 text-orange-200 border border-orange-500/60"
                    : "bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-orange-500"
                }`}
                title="Divide Beam at Midpoint (D)"
              >
                <svg
                  className={`w-5 h-5 ${
                    modelStore.isDivideBeamMode
                      ? "text-orange-300"
                      : "text-orange-400 group-hover:text-orange-300"
                  }`}
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  {/* Beam line */}
                  <line
                    x1="4"
                    y1="12"
                    x2="20"
                    y2="12"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />

                  {/* Start node */}
                  <circle cx="4" cy="12" r="2" fill="currentColor" />

                  {/* End node */}
                  <circle cx="20" cy="12" r="2" fill="currentColor" />

                  {/* Mid-node (divide point) - LARGER + GLOW */}
                  <circle
                    cx="12"
                    cy="12"
                    r="3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <circle cx="12" cy="12" r="1.5" fill="currentColor" />

                  {/* Divide indicator lines */}
                  <line
                    x1="10"
                    y1="10"
                    x2="14"
                    y2="10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <line
                    x1="10"
                    y1="14"
                    x2="14"
                    y2="14"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>

                <span
                  className={`text-xs font-medium ${
                    modelStore.isDivideBeamMode
                      ? "text-orange-200"
                      : "text-gray-300 group-hover:text-white"
                  }`}
                >
                  Mid Node
                </span>
              </button>

              {/* Capture Area */}
              <button
                onClick={handleCaptureClick}
                className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all group ${
                  isCaptureMode
                    ? "bg-gradient-to-br from-pink-500/30 to-pink-600/20 text-pink-200 border border-pink-500/60"
                    : "bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-pink-500"
                }`}
                title="Capture Area (Screenshot)"
              >
                <svg
                  className={`w-5 h-5 ${
                    isCaptureMode
                      ? "text-pink-300"
                      : "text-pink-400 group-hover:text-pink-300"
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {/* Camera body */}
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  {/* Camera lens */}
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <span
                  className={`text-xs font-medium ${
                    isCaptureMode
                      ? "text-pink-200"
                      : "text-gray-300 group-hover:text-white"
                  }`}
                >
                  Capture
                </span>
              </button>
            </div>
          </div>

          <div className="w-px h-16 bg-gradient-to-b from-transparent via-gray-600 to-transparent"></div>

          <div className="flex items-center gap-2">
            <div className="text-[10px] text-gray-500 font-semibold writing-mode-vertical h-16 flex items-center tracking-wide">
              LOAD
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleLoadViewClick}
                className="flex flex-col items-center gap-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-yellow-500 rounded-lg transition-all group"
              >
                <svg
                  className="w-5 h-5 text-yellow-400 group-hover:text-yellow-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeWidth="2" />
                  <path
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    strokeWidth="2"
                  />
                </svg>
                <span className="text-xs text-gray-300 group-hover:text-white font-medium">
                  View
                </span>
              </button>
              <button
                onClick={handleLoadAddClick}
                className="flex flex-col items-center gap-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-yellow-500 rounded-lg transition-all group"
              >
                <svg
                  className="w-5 h-5 text-yellow-400 group-hover:text-yellow-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 4v16m8-8H4"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="text-xs text-gray-300 group-hover:text-white font-medium">
                  Add
                </span>
              </button>
            </div>
          </div>

          <div className="w-px h-16 bg-gradient-to-b from-transparent via-gray-600 to-transparent"></div>

          {/* ==================== SUPPORT SECTION ==================== */}
          <div className="flex items-center gap-2">
            <div className="text-[10px] text-gray-500 font-semibold writing-mode-vertical h-16 flex items-center tracking-wide">
              SUPPORT
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSupportViewClick}
                className="flex flex-col items-center gap-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-orange-500 rounded-lg transition-all group"
              >
                <svg
                  className="w-5 h-5 text-orange-400 group-hover:text-orange-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeWidth="2" />
                  <path
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    strokeWidth="2"
                  />
                </svg>
                <span className="text-xs text-gray-300 group-hover:text-white font-medium">
                  View
                </span>
              </button>
              <button
                onClick={handleSupportAddClick}
                className="flex flex-col items-center gap-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-orange-500 rounded-lg transition-all group"
              >
                <svg
                  className="w-5 h-5 text-orange-400 group-hover:text-orange-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 4v16m8-8H4"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="text-xs text-gray-300 group-hover:text-white font-medium">
                  Add
                </span>
              </button>
            </div>
          </div>

          <div className="w-px h-16 bg-gradient-to-b from-transparent via-gray-600 to-transparent"></div>

          {/* ==================== SECTION SECTION ==================== */}
          <div className="flex items-center gap-2">
            <div className="text-[10px] text-gray-500 font-semibold writing-mode-vertical h-16 flex items-center tracking-wide">
              SECTION
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSectionViewClick}
                className="flex flex-col items-center gap-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-pink-500 rounded-lg transition-all group"
              >
                <svg
                  className="w-5 h-5 text-pink-400 group-hover:text-pink-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeWidth="2" />
                  <path
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    strokeWidth="2"
                  />
                </svg>
                <span className="text-xs text-gray-300 group-hover:text-white font-medium">
                  View
                </span>
              </button>
              <button
                onClick={handleSectionAddClick}
                className="flex flex-col items-center gap-1 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-pink-500 rounded-lg transition-all group"
              >
                <svg
                  className="w-5 h-5 text-pink-400 group-hover:text-pink-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 4v16m8-8H4"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <span className="text-xs text-gray-300 group-hover:text-white font-medium">
                  Add
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModelingMenu;
